import useApiRequest from "@/lib/hooks/use-request";
import { DealDetailsResponseDto } from "@/lib/interfaces/deals";
import OrganisationLinked from "@/page-components/contacts/organisations-linked";
import PersonLinked from "@/page-components/contacts/persons-linked";
import PageNotFound from "@/page-components/error/page-not-found";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/ui-components/ui/accordion";
import { useBreadcrumb } from "@/ui-components/ui/breadcrumb/breadcrumb-context";
import { Button } from "@/ui-components/ui/button";
import ConfirmationDialog from "@/ui-components/ui/confirmation-dialog";
import DataFetchParent from "@/ui-components/ui/data-fetch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/ui-components/ui/select";
import { Table, TableBody, TableCell, TableRow } from "@/ui-components/ui/table";
import TitleForm from "@/ui-components/ui/title-form";
import {
  AccordionContainer,
  AccordionContainerParent,
} from "@/ui-components/utils/menu-items/accordion-container-manager";
import { PageTitle, PageTitleContainer } from "@/ui-components/utils/menu-items/page-title";
import Tippy from "@tippyjs/react";
import { Building2, Check, Link2Off, Mail, MapPin, Pencil, Phone, Plus, User, X } from "lucide-react";
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import "tippy.js/dist/tippy.css";
import DealHistory from "../history";
import InformationForm from "../information-form";

const DealsItem = () => {
  const { id } = useParams();
  return <>{id ? <DealsItemFetcher id={id} /> : <PageNotFound />}</>;
};

const DealsItemFetcher = ({ id }: { id: string }) => {
  const [item, setItem] = useState<DealDetailsResponseDto>();
  const [isLoading, setIsLoading] = useState(false);
  const { apiRequest } = useApiRequest();
  const [reload, setReload] = useState(false);

  const getItem = useCallback(async () => {
    setIsLoading(true);
    const response = await apiRequest<DealDetailsResponseDto>(`deals/${id}`, "GET");
    setIsLoading(false);
    if (response.data) {
      setItem(response.data);
    }
  }, [apiRequest, id]);

  useEffect(() => {
    getItem();
  }, [getItem, reload]);

  return (
    <DataFetchParent
      isLoading={isLoading}
      data={item}
      renderElement={(loadedItem: DealDetailsResponseDto) => <DealDetails setReload={setReload} item={loadedItem} />}
    />
  );
};

const DealDetails = ({
  item,
  setReload,
}: {
  item: DealDetailsResponseDto;
  setReload: Dispatch<SetStateAction<boolean>>;
}) => {
  const { setTitle, setQuery } = useBreadcrumb();
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingInformation, setIsEditingInformation] = useState(false);
  const [showPersonSearch, setShowPersonSearch] = useState(false);
  const [showOrgaSearch, setShowOrgaSearch] = useState(false);
  const { apiRequest } = useApiRequest();

  useEffect(() => {
    setTitle(item.title);
    setQuery(`pipelineId=${item.belongsToPipelineId}`);
  }, [item, setTitle, setQuery]);

  const updateTitle = async (title: string) => {
    await apiRequest<DealDetailsResponseDto>(`deals/${item._id}`, "PATCH", {
      body: { title },
    });
    setIsEditing(false);
    setReload((prev) => !prev);
  };

  const assignOrga = async (organizationId: string, title: string) => {
    await apiRequest<DealDetailsResponseDto>(`deals/${item._id}`, "PATCH", {
      body: {
        belongsToOrganizationId: organizationId,
      },
    });
    setShowOrgaSearch(false);
    setReload((prev) => !prev);
  };

  const removeLinkedOrga = async () => {
    await apiRequest<DealDetailsResponseDto>(`deals/${item._id}/unset-organization`, "PATCH");
    setReload((prev) => !prev);
  };

  const assignPerson = async (userId: string, title: string) => {
    await apiRequest<DealDetailsResponseDto>(`deals/${item._id}`, "PATCH", {
      body: { belongsToPersonId: userId },
    });
    setShowPersonSearch(false);
    setReload((prev) => !prev);
  };

  const removeLinkedPerson = async () => {
    await apiRequest<DealDetailsResponseDto>(`deals/${item._id}/unset-person`, "PATCH");
    setReload((prev) => !prev);
  };

  const updateInformation = async (information: string) => {
    await apiRequest<DealDetailsResponseDto>(`deals/${item._id}`, "PATCH", {
      body: { information },
    });
    setIsEditingInformation(false);
    setReload((prev) => !prev);
  };

  return (
    <div className="h-full flex flex-col">
      <PageTitleContainer>
        <div className="flex flex-row justify-between items-center min-h-10">
          <PageTitle>
            <div className="flex flex-row gap-2 items-center">
              {isEditing ? (
                <TitleForm
                  onSubmit={(values) => updateTitle(values.title)}
                  defaultValue={item.title}
                  onCancel={() => setIsEditing(false)}
                />
              ) : (
                <div className="flex items-center gap-2">
                  <span>{item.title}</span>
                  {item.isEditable && (
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setIsEditing(true)}>
                      <Pencil size={16} />
                    </Button>
                  )}
                </div>
              )}
            </div>
          </PageTitle>
          <div className="flex flex-row gap-2">
            <DealTopBarMenuItems item={item} setReload={setReload} />
          </div>
        </div>
      </PageTitleContainer>
      <div className="grow">
        <AccordionContainerParent>
          <AccordionContainer>
            <div className="flex flex-col gap-2">
              <Accordion type="multiple" className="space-y-4">
                <AccordionItem value="summary" className="border rounded-lg">
                  <AccordionTrigger className="px-4">Zusammenfassung</AccordionTrigger>
                  <AccordionContent className="px-4 pb-4 m-0">
                    <div className="py-1 border-b last:border-b-0">
                      <div className="flex justify-between">
                        {item.summaryPreview?.personSummary ? (
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4" />
                            <Link
                              to={`/contacts/persons/${item.summaryPreview.personSummary._id}`}
                              className="text-blue-600 hover:text-blue-800 hover:underline"
                            >
                              {item.summaryPreview.personSummary.title}
                            </Link>
                          </div>
                        ) : (
                          <div className="text-gray-500 italic flex items-center gap-2">
                            <User className="h-4 w-4" />
                            Keine Person verknüpft
                            <Link2Off className="h-4 w-4" />
                          </div>
                        )}
                        {item.isEditable &&
                          (item.summaryPreview?.personSummary ? (
                            <ConfirmationDialog
                              onConfirm={removeLinkedPerson}
                              title="Verbindung trennen"
                              description="Sind Sie sicher, dass Sie diese Verbindung trennen möchten?"
                              variant="destructive"
                            >
                              <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-800">
                                <X className="h-4 w-4" />
                              </Button>
                            </ConfirmationDialog>
                          ) : (
                            <Button
                              onClick={() => setShowPersonSearch(true)}
                              variant="ghost"
                              size="sm"
                              className="text-blue-600 hover:text-blue-800"
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          ))}
                      </div>
                      {showPersonSearch && (
                        <div className="mt-2">
                          <PersonLinked
                            onAssign={assignPerson}
                            onRemove={removeLinkedPerson}
                            item={item.personPreview}
                          />
                        </div>
                      )}
                    </div>
                    <div className="py-1 border-b last:border-b-0">
                      <div className="flex justify-between">
                        {item.summaryPreview?.organizationSummary ? (
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4" />
                            <Link
                              to={`/contacts/organisations/${item.summaryPreview.organizationSummary._id}`}
                              className="text-blue-600 hover:text-blue-800 hover:underline"
                            >
                              {item.summaryPreview.organizationSummary.title}
                            </Link>
                          </div>
                        ) : (
                          <div className="text-gray-500 italic flex items-center gap-2">
                            <Building2 className="h-4 w-4" />
                            Kein Unternehmen verknüpft
                            <Link2Off className="h-4 w-4" />
                          </div>
                        )}
                        {item.isEditable &&
                          (item.summaryPreview?.organizationSummary ? (
                            <ConfirmationDialog
                              onConfirm={removeLinkedOrga}
                              title="Verbindung trennen"
                              description="Sind Sie sicher, dass Sie diese Verbindung trennen möchten?"
                              variant="destructive"
                            >
                              <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-800">
                                <X className="h-4 w-4" />
                              </Button>
                            </ConfirmationDialog>
                          ) : (
                            <Button
                              onClick={() => setShowOrgaSearch(true)}
                              variant="ghost"
                              size="sm"
                              className="text-blue-600 hover:text-blue-800"
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          ))}
                      </div>
                      {showOrgaSearch && (
                        <div className="mt-2">
                          <OrganisationLinked
                            onAssign={assignOrga}
                            onRemove={removeLinkedOrga}
                            item={item.organizationPreview}
                          />
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="information" className="border rounded-lg">
                  <AccordionTrigger className="px-4">Informationen</AccordionTrigger>
                  <AccordionContent className="px-4 pb-4 m-0 min-h-10">
                    {isEditingInformation ? (
                      <InformationForm
                        onSubmit={(values) => updateInformation(values.information)}
                        defaultValue={item.information}
                        onCancel={() => setIsEditingInformation(false)}
                      />
                    ) : (
                      <div className="py-1 relative pl-0">
                        {item.isEditable && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="absolute top-0 right-0 h-8 w-8 p-0"
                            onClick={() => setIsEditingInformation(true)}
                          >
                            <Pencil size={16} />
                          </Button>
                        )}
                        {item.information ? (
                          <p dangerouslySetInnerHTML={{ __html: item.information.split("\n").join("<br />") }} />
                        ) : (
                          <p className="text-gray-500 italic flex items-center gap-2 h-8">
                            Keine Informationen eingetragen
                          </p>
                        )}
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="person" className="border rounded-lg">
                  <AccordionTrigger className="px-4">Personen Details</AccordionTrigger>
                  <AccordionContent className="px-4 pb-4 m-0">
                    <Table className="w-full">
                      <TableBody>
                        <TableRow>
                          <TableCell className="font-medium pl-0 w-1/3">
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4" />
                              Name
                            </div>
                          </TableCell>
                          <TableCell className="w-2/3">
                            {item.personPreview?.fullName ? (
                              <Tippy content={item.personPreview.fullName}>
                                <div className="truncate max-w-[250px]">{item.personPreview.fullName}</div>
                              </Tippy>
                            ) : (
                              <span>-</span>
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium pl-0 w-1/3">
                            <div className="flex items-center gap-2">
                              <Mail className="h-4 w-4" />
                              Email
                            </div>
                          </TableCell>
                          <TableCell className="w-2/3">
                            {item.personPreview?.mail ? (
                              <Tippy content={item.personPreview.mail}>
                                <div className="truncate max-w-[250px]">
                                  <Link
                                    className="flex items-center gap-2 cursor-pointer"
                                    to={`mailto:${item.personPreview.mail}`}
                                    target="_blank"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <Button variant="link" className="flex items-center gap-2 h-fit p-0">
                                      <Mail className="h-4 w-4" />
                                      <span className="truncate">{item.personPreview.mail}</span>
                                    </Button>
                                  </Link>
                                </div>
                              </Tippy>
                            ) : (
                              <span>-</span>
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium pl-0 w-1/3">
                            <div className="flex items-center gap-2">
                              <Phone className="h-4 w-4" />
                              Telefon
                            </div>
                          </TableCell>
                          <TableCell className="w-2/3">
                            {item.personPreview?.phone ? (
                              <Tippy content={item.personPreview.phone}>
                                <div className="truncate max-w-[250px]">
                                  <Link
                                    className="flex items-center gap-2 cursor-pointer"
                                    to={`tel:${item.personPreview.phone}`}
                                    target="_blank"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <Button variant="link" className="flex items-center gap-2 h-fit p-0">
                                      <Phone className="h-4 w-4" />
                                      <span className="truncate">{item.personPreview.phone}</span>
                                    </Button>
                                  </Link>
                                </div>
                              </Tippy>
                            ) : (
                              <span>-</span>
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium pl-0 w-1/3">Straße</TableCell>
                          <TableCell className="w-2/3">
                            {item.personPreview?.street ? (
                              <Tippy content={item.personPreview.street}>
                                <div className="truncate max-w-[250px]">{item.personPreview.street}</div>
                              </Tippy>
                            ) : (
                              <span>-</span>
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium pl-0 w-1/3">Postleitzahl</TableCell>
                          <TableCell className="w-2/3">
                            {item.personPreview?.postalCode ? (
                              <Tippy content={item.personPreview.postalCode}>
                                <div className="truncate max-w-[250px]">{item.personPreview.postalCode}</div>
                              </Tippy>
                            ) : (
                              <span>-</span>
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium pl-0 w-1/3">Stadt</TableCell>
                          <TableCell className="w-2/3">
                            {item.personPreview?.city ? (
                              <Tippy content={item.personPreview.city}>
                                <div className="truncate max-w-[250px]">{item.personPreview.city}</div>
                              </Tippy>
                            ) : (
                              <span>-</span>
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium pl-0 w-1/3">Land</TableCell>
                          <TableCell className="w-2/3">
                            {item.personPreview?.country ? (
                              <Tippy content={item.personPreview.country}>
                                <div className="truncate max-w-[250px]">{item.personPreview.country}</div>
                              </Tippy>
                            ) : (
                              <span>-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="organisation" className="border rounded-lg">
                  <AccordionTrigger className="px-4">Unternehmen Details</AccordionTrigger>
                  <AccordionContent className="px-4 pb-4 m-0">
                    <Table className="w-full">
                      <TableBody>
                        <TableRow>
                          <TableCell className="font-medium pl-0 w-1/3">
                            <div className="flex items-center gap-2">
                              <Building2 className="h-4 w-4" />
                              Name
                            </div>
                          </TableCell>
                          <TableCell className="w-2/3">
                            {item.organizationPreview?.name ? (
                              <Tippy content={item.organizationPreview.name}>
                                <div className="truncate max-w-[250px]">
                                  <span className="truncate">{item.organizationPreview.name}</span>
                                </div>
                              </Tippy>
                            ) : (
                              <span>-</span>
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium pl-0 w-1/3">
                            <div className="flex items-center gap-2">
                              <MapPin className="h-4 w-4" />
                              Adresse
                            </div>
                          </TableCell>
                          <TableCell className="w-2/3">
                            {item.organizationPreview?.address ? (
                              <Tippy content={item.organizationPreview.address}>
                                <div className="truncate max-w-[250px]">{item.organizationPreview.address}</div>
                              </Tippy>
                            ) : (
                              <span>-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="sale" className="border rounded-lg">
                  <AccordionTrigger className="px-4">Sale</AccordionTrigger>
                  <AccordionContent className="px-4 pb-4 m-0">
                    {item.salePreview ? (
                      <Table className="w-full">
                        <TableBody>
                          <TableRow>
                            <TableCell className="font-medium pl-0 w-1/3">
                              <div className="flex items-center gap-2">
                                <Check className="h-4 w-4" />
                                Sale
                              </div>
                            </TableCell>
                            <TableCell className="w-2/3">
                              <Tippy content={item.salePreview.title}>
                                <div className="truncate max-w-[250px]">
                                  <Link to={`/sales/${item.salePreview._id}`}>
                                    <Button variant="link" className="flex items-center gap-2 h-fit p-0">
                                      <span className="truncate">{item.salePreview.title}</span>
                                    </Button>
                                  </Link>
                                </div>
                              </Tippy>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    ) : (
                      <>
                        {item.isEditable ? (
                          <Link to={`/sales/create?dealId=${item._id}`}>
                            <Button variant="link" className="m-0 p-0">
                              Erstelle einen Sale
                            </Button>
                          </Link>
                        ) : (
                          <p className="text-sm text-gray-500">
                            Kein Sale erstellt - bist du sicher, dass du der Besitzer des Deals bist?
                          </p>
                        )}
                      </>
                    )}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </AccordionContainer>
          <div className="w-full flex flex-col gap-4">
            {/* <div className="flex flex-col items-center justify-center h-[60vh] text-center">
              <div className="text-6xl mb-6">🚀</div>
              <h2 className="text-2xl font-bold mb-2">Coming Soon</h2>
              <p className="text-gray-500">Der Aktivitätenverlauf wird bald zur Verfügung stehen</p>
            </div> */}
            <DealHistory item={item} setReload={setReload} />
          </div>
        </AccordionContainerParent>
      </div>
    </div>
  );
};

const DealTopBarMenuItems = ({
  item,
  setReload,
}: {
  item: DealDetailsResponseDto;
  setReload: Dispatch<SetStateAction<boolean>>;
}) => {
  const { apiRequest } = useApiRequest();

  const toggleWin = async (type: "win" | "lose") => {
    await apiRequest<DealDetailsResponseDto>(`deals/${item._id}/${type}`, "PATCH");
    setReload((prev) => !prev);
  };

  const setOwner = async (userId: string) => {
    await apiRequest<DealDetailsResponseDto>(`deals/${item._id}/owner`, "PATCH", {
      body: { userId: userId },
    });
    setReload((prev) => !prev);
  };

  return (
    <>
      <div className="flex flex-row gap-2 items-center">
        <p className="text-sm text-gray-500">Besitzer:</p>
        <Select value={item.belongsToUserId} onValueChange={(value) => setOwner(value)} disabled={!item.isEditable}>
          <SelectTrigger>
            <SelectValue placeholder="Besitzer ändern" />
          </SelectTrigger>
          <SelectContent>
            {item.selectableEntities?.users.map((user) => (
              <SelectItem key={user._id} value={user._id}>
                {user.value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button onClick={() => toggleWin("win")} disabled={!item.isEditable}>
        <Check />
        Gewonnen
      </Button>
      <Button onClick={() => toggleWin("lose")} variant="destructive" disabled={!item.isEditable}>
        <X />
        Verloren
      </Button>
    </>
  );
};

export default DealsItem;
